import React from 'react';
import { useTranslation } from 'react-i18next';

const Privacy = () => {
    const { t } = useTranslation();

    return (
        <div className="page-transition">
            <div className="container mx-auto px-4 py-12 max-w-4xl">
                <h1 id="privacy-top" className="text-4xl font-bold mb-8 text-center">{t('privacy.title')}</h1>

                <div className="prose max-w-none">
                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">{t('privacy.lastUpdated')}</h2>
                        <p className="text-gray-600 mb-8">July 2024</p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">{t('privacy.introduction.title')}</h2>
                        <p className="text-gray-700 mb-4">
                            {t('privacy.introduction.description')}
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">{t('privacy.dataCollected.title')}</h2>
                        <p className="text-gray-700 mb-4">{t('privacy.dataCollected.description')}</p>
                        <div className="bg-blue-50 p-6 rounded-lg">
                            <h3 className="text-lg font-semibold mb-4">{t('privacy.dataCollected.typesTitle')}</h3>
                            <ul className="space-y-3 text-gray-700">
                                <li>
                                    <strong>{t('privacy.dataCollected.accountData')}:</strong> {t('privacy.dataCollected.accountDataDesc')}
                                </li>
                                <li>
                                    <strong>{t('privacy.dataCollected.patientData')}:</strong> {t('privacy.dataCollected.patientDataDesc')}
                                </li>
                                <li>
                                    <strong>{t('privacy.dataCollected.measurementData')}:</strong> {t('privacy.dataCollected.measurementDataDesc')}
                                </li>
                                <li>
                                    <strong>{t('privacy.dataCollected.usageData')}:</strong> {t('privacy.dataCollected.usageDataDesc')}
                                </li>
                                <li>
                                    <strong>{t('privacy.dataCollected.communicationData')}:</strong> {t('privacy.dataCollected.communicationDataDesc')}
                                </li>
                            </ul>
                        </div>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">{t('privacy.dataUsage.title')}</h2>
                        <p className="text-gray-700 mb-4">{t('privacy.dataUsage.description')}</p>
                        <div className="space-y-3 text-gray-700 ml-4">
                            <div className="flex items-start">
                                <span className="text-blue-600 mr-3 mt-1">•</span>
                                <p>{t('privacy.dataUsage.purpose1')}</p>
                            </div>
                            <div className="flex items-start">
                                <span className="text-blue-600 mr-3 mt-1">•</span>
                                <p>{t('privacy.dataUsage.purpose2')}</p>
                            </div>
                            <div className="flex items-start">
                                <span className="text-blue-600 mr-3 mt-1">•</span>
                                <p>{t('privacy.dataUsage.purpose3')}</p>
                            </div>
                            <div className="flex items-start">
                                <span className="text-blue-600 mr-3 mt-1">•</span>
                                <p>{t('privacy.dataUsage.purpose4')}</p>
                            </div>
                            <div className="flex items-start">
                                <span className="text-blue-600 mr-3 mt-1">•</span>
                                <p>{t('privacy.dataUsage.purpose5')}</p>
                            </div>
                        </div>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">{t('privacy.dataSecurity.title')}</h2>
                        <p className="text-gray-700 mb-4">
                            {t('privacy.dataSecurity.description')}
                        </p>
                        <ul className="space-y-2 text-gray-700 ml-4">
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-3 mt-1">•</span>
                                <span>{t('privacy.dataSecurity.measure1')}</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-3 mt-1">•</span>
                                <span>{t('privacy.dataSecurity.measure2')}</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-3 mt-1">•</span>
                                <span>{t('privacy.dataSecurity.measure3')}</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-3 mt-1">•</span>
                                <span>{t('privacy.dataSecurity.measure4')}</span>
                            </li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">{t('privacy.dataRetention.title')}</h2>
                        <p className="text-gray-700 mb-4">
                            {t('privacy.dataRetention.description')}
                        </p>
                    </section>

                    <section id="hipaa" className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">{t('privacy.hipaa.title')}</h2>
                        <p className="text-gray-700 mb-4">
                            {t('privacy.hipaa.description')}
                        </p>
                        <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
                            <p className="text-gray-700">
                                {t('privacy.hipaa.commitment')}
                            </p>
                        </div>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">{t('privacy.gdpr.title')}</h2>
                        <p className="text-gray-700 mb-4">
                            {t('privacy.gdpr.description')}
                        </p>
                        <ul className="space-y-2 text-gray-700 ml-4">
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-3 mt-1">•</span>
                                <span><strong>{t('privacy.gdpr.right1Title')}:</strong> {t('privacy.gdpr.right1Desc')}</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-3 mt-1">•</span>
                                <span><strong>{t('privacy.gdpr.right2Title')}:</strong> {t('privacy.gdpr.right2Desc')}</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-3 mt-1">•</span>
                                <span><strong>{t('privacy.gdpr.right3Title')}:</strong> {t('privacy.gdpr.right3Desc')}</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-3 mt-1">•</span>
                                <span><strong>{t('privacy.gdpr.right4Title')}:</strong> {t('privacy.gdpr.right4Desc')}</span>
                            </li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">{t('privacy.sharing.title')}</h2>
                        <p className="text-gray-700 mb-4">
                            {t('privacy.sharing.description')}
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">{t('privacy.cookies.title')}</h2>
                        <p className="text-gray-700 mb-4">
                            {t('privacy.cookies.description')}
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">{t('privacy.userRights.title')}</h2>
                        <p className="text-gray-700 mb-4">
                            {t('privacy.userRights.description')}
                        </p>
                        <ul className="space-y-2 text-gray-700 ml-4">
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-3 mt-1">•</span>
                                <span>{t('privacy.userRights.right1')}</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-3 mt-1">•</span>
                                <span>{t('privacy.userRights.right2')}</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-3 mt-1">•</span>
                                <span>{t('privacy.userRights.right3')}</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-blue-600 mr-3 mt-1">•</span>
                                <span>{t('privacy.userRights.right4')}</span>
                            </li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">{t('privacy.contact.title')}</h2>
                        <p className="text-gray-700 mb-4">
                            {t('privacy.contact.description')}
                        </p>
                        <div className="bg-gray-100 p-6 rounded-lg">
                            <p className="text-gray-700 mb-2">
                                <strong>SOKBA</strong>
                            </p>
                            <p className="text-gray-700 mb-2">Email: info@sokba.net</p>
                            <p className="text-gray-700">Web: www.sokba.net</p>
                        </div>
                    </section>

                    <section className="mb-8 p-6 bg-blue-50 rounded-lg">
                        <p className="text-gray-700 text-sm">
                            <strong>{t('privacy.changes.title')}:</strong> {t('privacy.changes.description')}
                        </p>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default Privacy;
